/* The step panel's cards, one set per kind of step: a tool's command or arguments and its
   result, a model call's new context and reply, a wait on a person, a run's question,
   answer, what it changed and where its time went. Each card reads formatted; its JSON view
   is that part of the step's span as the trace holds it (`span.record`), never a rebuilt one. */
import React, { useState } from "react";
import { Check, ChevronRight, CircleAlert, CircleX, CornerDownRight, LoaderCircle, SquareTerminal } from "lucide-react";
import { toast } from "sonner";

import Fold from "@/shared/components/Fold";
import { ListCard } from "@/shared/components/ListCard";
import CodeBlock, { CopyButton } from "@/shared/components/markdown/CodeBlock";
import { plural, Segmented } from "@/shared/components/teams-ui";
import { IconButton } from "@/shared/components/ui-kit";
import { fmtMs, fmtTokens } from "@/shared/lib/runs";
import { callIdOf, callsOf, contextOf, curlForGeneration, errorReadBy, purposeOf, stepsForCalls, wasStopped } from "../traceModel";
import { EmptyNote, KindIcon, TruncText } from "./atoms";
import { money } from "./money";
import { ContentBlocks, JsonView, PrettyView, RoleBlock, ToolsField } from "./payloads";
import { EffectLedger, SpendList } from "./views";

import type { Run } from "@/shared/types";
import type {
  AssistantPayload, BillableSpan, ChatMessage, ContentBlock, EffectEntry, LlmInputPayload, Span, ToolInputPayload, ToolOutputPayload,
  TraceAnalytics, TurnInputPayload,
} from "../types";

/** What the panel keeps as you move between steps: cards as fields or JSON, the record open. */
export interface PanelPrefs {
  view: "fields" | "json";
  raw: boolean;
}

/** The run as a whole, worked out once by the trace and read by its panel. */
export interface TraceOverview {
  analytics: TraceAnalytics;
  effects: EffectEntry[];
  spend: { items: BillableSpan[]; total: number };
  /** Milliseconds between steps when nothing was recorded. */
  idle: number;
}

/** What every card needs besides its own step: the run around it. */
export interface StepContext {
  run: Run;
  overview: TraceOverview;
  /** Every step that is not the run or an attempt, in the order they ran. */
  steps: Span[];
  /** The model call before this one in the same attempt; null for an attempt's first. */
  previousCall: (span: Span) => Span | null;
  onSelect: (id: string) => void;
  prefs: PanelPrefs;
  onPrefs: (change: Partial<PanelPrefs>) => void;
}

/* ---------------------------------- atoms ---------------------------------- */

const json = (value: unknown) => JSON.stringify(value, null, 2);

/** A card's body when there is nothing to show yet, or nothing at all. */
function CardEmpty({ children }: { children?: React.ReactNode }) {
  return <div className="px-4 py-2.5"><EmptyNote>{children}</EmptyNote></div>;
}

const isEmpty = (v: unknown) => (Array.isArray(v) ? v.length === 0 : v !== null && typeof v === "object" && Object.keys(v).length === 0);

function Value({ value }: { value: unknown }) {
  if (value === true) return <span className="inline-flex items-center gap-1 font-mono text-[12.5px] text-[var(--success)]"><Check className="w-3 h-3" strokeWidth={2.25} />true</span>;
  if (value === null || value === undefined) return <span className="font-mono text-[12.5px] text-[var(--text-quaternary)]">null</span>;
  if (isEmpty(value)) return <span className="text-[12.5px] text-[var(--text-quaternary)]">empty</span>;
  if (typeof value === "object") return <JsonView data={value} full />;
  return <TruncText mono text={String(value)} limit={600} className="text-[12.5px] leading-[1.6] text-[var(--text-primary)]" />;
}

/** One row per key: the name on the left, its value whole on the right. */
export function Fields({ data }: { data: Record<string, unknown> }) {
  return (
    <div className="divide-y divide-[var(--hairline)]">
      {Object.entries(data).map(([key, value]) => (
        <div key={key} className="flex gap-3 px-4 py-2 min-w-0">
          <span title={key} className="w-[96px] shrink-0 pt-px text-[12px] text-[var(--text-tertiary)] truncate">{key}</span>
          <span className="min-w-0 flex-1 [overflow-wrap:anywhere]"><Value value={value} /></span>
        </div>
      ))}
    </div>
  );
}

/** A card that reads formatted or, by the Fields / JSON switch on its strip, as its part of the
    step's span. The switch is the panel's, so the choice holds from step to step. `full` adds a
    third view: the whole request a model call was sent, rebuilt from the run. */
function PayloadCard({ ctx, label, raw, full, copy, extra, danger = false, children }: {
  ctx: StepContext;
  label: React.ReactNode;
  /** Its part of the step's span; undefined when there is none to show. */
  raw?: unknown;
  full?: unknown;
  /** What Copy copies in the formatted view; the shown JSON otherwise. */
  copy?: string;
  /** More controls on the strip, before Copy. */
  extra?: React.ReactNode;
  danger?: boolean;
  children?: React.ReactNode;
}) {
  const [showFull, setShowFull] = useState(false);
  const hasRaw = raw !== undefined && raw !== null;
  const view = showFull && full !== undefined ? "full" : hasRaw ? ctx.prefs.view : "fields";
  const shown = view === "full" ? full : view === "json" ? raw : undefined;
  const text = shown !== undefined ? json(shown) : copy ?? (hasRaw ? json(raw) : "");
  const options = [
    { value: "fields", label: "Fields" },
    ...(full !== undefined ? [{ value: "full", label: "Full prompt" }] : []),
    { value: "json", label: "JSON" },
  ];
  return (
    <ListCard
      label={label}
      danger={danger}
      action={
        <span className="flex items-center gap-1">
          {hasRaw && (
            <Segmented
              role="radiogroup"
              label="Show as"
              value={view}
              options={options}
              onChange={(v) => {
                setShowFull(v === "full");
                if (v !== "full") ctx.onPrefs({ view: v as PanelPrefs["view"] });
              }}
            />
          )}
          {extra}
          {text && <CopyButton text={text} label="Copy" />}
        </span>
      }
    >
      {shown !== undefined ? <div className="px-4 py-3"><JsonView data={shown} full /></div> : children}
    </ListCard>
  );
}

function ErrorCard({ text, after }: { text: string; after?: React.ReactNode }) {
  return (
    <ListCard danger label={<span className="inline-flex items-center gap-1.5"><CircleAlert className="w-3.5 h-3.5" strokeWidth={1.75} />Error</span>} action={<CopyButton text={text} label="Copy error" />}>
      <pre className="m-0 px-4 py-3 font-mono text-[12.5px] leading-[1.6] text-[var(--danger)] whitespace-pre-wrap [overflow-wrap:anywhere]">{text}</pre>
      {after && <div className="flex items-center gap-1.5 px-4 py-2.5 text-[12px] text-[var(--danger)]">{after}</div>}
    </ListCard>
  );
}

/** A tool call as a card you can open: the tool, what it was for, how it went. */
function CallCard({ name, step, onSelect }: { name: string; step: Span | null; onSelect: (id: string) => void }) {
  return (
    <button
      type="button"
      disabled={!step}
      onClick={() => step && onSelect(step.id)}
      className="press w-full flex items-center gap-2.5 h-[46px] pl-[7px] pr-2.5 rounded-[12px] ring-1 ring-[var(--hairline-strong)] bg-[var(--surface)] hover:bg-[var(--accent-hover)] text-left disabled:pointer-events-none"
    >
      <KindIcon kind="tool" status={step?.status} tool={step?.raw?.tool} size={28} />
      <span className="shrink-0 font-mono text-[12.5px] font-medium text-[var(--text-primary)]">{name}</span>
      <span className="min-w-0 flex-1 truncate text-[12.5px] text-[var(--text-tertiary)]">{step && (purposeOf(step) ?? step.label)}</span>
      {step?.status === "error" ? (
        <CircleX className="w-3.5 h-3.5 shrink-0 text-[var(--danger)]" strokeWidth={1.75} aria-label="Failed" />
      ) : step?.status === "pending" ? (
        <LoaderCircle className="w-3.5 h-3.5 shrink-0 animate-spin text-[var(--text-tertiary)]" strokeWidth={1.75} aria-label="Running" />
      ) : step && !wasStopped(step) ? (
        <Check className="w-3.5 h-3.5 shrink-0 text-[var(--success)]" strokeWidth={2.25} aria-label="Done" />
      ) : null}
      {step && <span className="shrink-0 font-mono text-[11.5px] tabular-nums text-[var(--text-tertiary)]">{fmtMs(step.dur)}</span>}
      <ChevronRight className="w-3.5 h-3.5 shrink-0 text-[var(--text-quaternary)]" strokeWidth={1.75} />
    </button>
  );
}

/* ---------------------------------- tools ---------------------------------- */

/** A tool's result text as fields, when it is a JSON object. */
function parsedResult(text: string | null | undefined): Record<string, unknown> | null {
  const t = text?.trim();
  if (!t || !t.startsWith("{")) return null;
  try {
    const value = JSON.parse(t);
    return value && typeof value === "object" && !Array.isArray(value) ? value : null;
  } catch {
    return null;
  }
}

export function ToolCards({ span, ctx }: { span: Span; ctx: StepContext }) {
  const input = span.io.input as ToolInputPayload | null;
  const output = span.io.output as ToolOutputPayload | null;
  const args = input?.arguments ?? {};
  const command = typeof args.command === "string" ? args.command : null;
  // a short description is the panel's sentence; the rest are the arguments
  const rest = Object.fromEntries(Object.entries(args).filter(([k]) => !(k === "description" && purposeOf(span)) && !(command && k === "command")));
  const reader = span.status === "error" ? errorReadBy(span, ctx.steps) : null;
  const fields = parsedResult(output?.result);
  return (
    <>
      {span.status === "error" && (
        <ErrorCard
          text={output?.error ?? span.meta.status_message ?? "The tool failed."}
          after={reader && <><CornerDownRight className="w-3 h-3 shrink-0" strokeWidth={1.75} />The model read this in its next call.</>}
        />
      )}
      {command && <CodeBlock code={command} lang="bash" title="Command" />}
      {(!command || Object.keys(rest).length > 0) && (
        <PayloadCard ctx={ctx} label={command ? "Other arguments" : "Input"} raw={span.record?.input}>
          {Object.keys(rest).length ? <Fields data={rest} /> : <CardEmpty>No arguments.</CardEmpty>}
        </PayloadCard>
      )}
      {span.status !== "error" && (
        <PayloadCard ctx={ctx} label="Result" raw={span.record?.output} copy={output?.result ?? undefined}>
          {span.status === "pending" ? (
            <CardEmpty>Still running.</CardEmpty>
          ) : wasStopped(span) ? (
            <CardEmpty>The run was stopped before it returned.</CardEmpty>
          ) : fields ? (
            <Fields data={fields} />
          ) : output?.result ? (
            <pre className="m-0 px-4 py-3 font-mono text-[12.5px] leading-[1.6] text-[var(--text-primary)] whitespace-pre-wrap [overflow-wrap:anywhere]">
              <TruncText text={output.result} limit={4000} />
            </pre>
          ) : (
            <CardEmpty>Returned nothing.</CardEmpty>
          )}
        </PayloadCard>
      )}
    </>
  );
}

/* ------------------------------- model calls ------------------------------- */

const textOf = (content: ContentBlock[] | undefined) =>
  (content ?? []).filter((b) => b.type === "text" && b.text).map((b) => b.text).join("\n\n");

/** Who a message is from, in the words a person uses. */
function speaker(m: ChatMessage, ctx: StepContext): string {
  if (m.role === "assistant") return "Model";
  const result = m.content.find((b) => b.type === "tool_result");
  if (!result) return "Person";
  const step = ctx.steps.find((s) => callIdOf(s) === result.tool_use_id);
  return `${step?.name ?? "Tool"} result`;
}

function Message({ m, ctx }: { m: ChatMessage; ctx: StepContext }) {
  return (
    <div className="px-4 py-2.5 min-w-0">
      <div className="text-[11.5px] text-[var(--text-tertiary)] mb-1">{speaker(m, ctx)}</div>
      <div className="min-w-0 break-words"><ContentBlocks content={m.content} expanded /></div>
    </div>
  );
}

export function ModelCards({ span, ctx }: { span: Span; ctx: StepContext }) {
  const input = span.io.input as LlmInputPayload | null;
  const output = span.io.output as (AssistantPayload & { error?: string }) | null;
  const context = contextOf(span, ctx.previousCall(span));
  const [earlierOpen, setEarlierOpen] = useState(false);
  const failed = span.status === "error" ? output?.error ?? span.meta.status_message ?? "The model call failed." : null;
  const calls = callsOf(span);
  const steps = stepsForCalls(ctx.steps, calls, span);
  const said = textOf(output?.content);
  const cached = output?.usage?.cache_read_input_tokens ?? 0;
  const earlierCount = context.earlier.length + context.unrecorded;
  // the request as the model got it, for the card's Full prompt view
  const request = input && { model: input.model, system: input.system, tools: input.tools, messages: input.messages };
  const curl = input && (
    <IconButton
      type="button"
      title="Copy as curl"
      aria-label="Copy as curl"
      onClick={() => {
        navigator.clipboard?.writeText(curlForGeneration(span.io));
        toast.success("Copied as curl", { description: "Replays this exact model call against the API." });
      }}
    >
      <SquareTerminal className="w-3.5 h-3.5" strokeWidth={1.75} />
    </IconButton>
  );
  const quiet = "font-normal text-[var(--text-quaternary)]";
  return (
    <>
      {failed && <ErrorCard text={failed} />}
      <PayloadCard
        ctx={ctx}
        label={context.first
          ? <>Prompt <span className={quiet}>{plural(context.length, "message")}</span></>
          : <>New in this call <span className={quiet}>{context.fresh.length}{context.grew ? ` · ${context.grew > 0 ? "+" : ""}${fmtTokens(context.grew)} tokens` : ""}</span></>}
        raw={span.record?.input}
        full={request ?? undefined}
        extra={curl}
      >
        {context.fresh.length ? context.fresh.map((m, i) => <Message key={i} m={m} ctx={ctx} />) : <CardEmpty>Nothing new since the last call.</CardEmpty>}
        {(earlierCount > 0 || input?.system || (input?.tools?.length ?? 0) > 0) && (
          <div>
            <button
              type="button"
              aria-expanded={earlierOpen}
              onClick={() => setEarlierOpen((v) => !v)}
              className="press w-full flex items-center gap-1.5 px-4 py-2.5 text-left text-[12px] text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--accent-hover)]"
            >
              <ChevronRight className={`w-3 h-3 shrink-0 transition-transform [transition-duration:300ms] ${earlierOpen ? "rotate-90" : ""}`} strokeWidth={2} />
              {[
                earlierCount ? plural(earlierCount, "earlier message") : null,
                input?.system ? "system prompt" : null,
                input?.tools?.length ? `${input.tools.length} tools offered` : null,
              ].filter(Boolean).join(" · ")}
              {cached > 0 && <span className="text-[var(--text-quaternary)]">· {fmtTokens(cached)} tokens from cache</span>}
            </button>
            <Fold open={earlierOpen}>
              <div className="px-4 pb-3 space-y-2">
                {input?.system && (
                  <div className="py-1.5">
                    <div className="text-[11.5px] text-[var(--text-tertiary)] mb-1">System prompt</div>
                    <TruncText text={input.system} limit={600} className="text-[12.5px] leading-[1.6] text-[var(--text-secondary)]" />
                  </div>
                )}
                <ToolsField tools={input?.tools} />
                {context.unrecorded > 0 && (
                  <p className="m-0 py-1 text-[12px] text-[var(--text-tertiary)]">
                    {plural(context.unrecorded, "earlier message")} of this chat went to the model too; the trace keeps only this turn's.
                  </p>
                )}
                {context.earlier.map((m, i) => <RoleBlock key={i} role={speaker(m, ctx)} content={m.content} />)}
              </div>
            </Fold>
          </div>
        )}
      </PayloadCard>
      {!failed && (
        <PayloadCard ctx={ctx} label={calls.length ? "Reply" : "Answer"} raw={span.record?.output} copy={said || undefined}>
          {span.status === "pending" && !said && !calls.length ? (
            <CardEmpty>Writing its reply.</CardEmpty>
          ) : wasStopped(span) && !said && !calls.length ? (
            <CardEmpty>The run was stopped before it replied.</CardEmpty>
          ) : (
            <>
              {said && <div className="px-4 py-3 text-[13.5px] leading-[1.6] text-[var(--text-primary)] whitespace-pre-wrap break-words">{said}</div>}
              {calls.length > 0 && (
                <div className={`px-2.5 pb-2.5 space-y-1.5 ${said ? "" : "pt-2.5"}`}>
                  {calls.map((c, i) => <CallCard key={c.id} name={c.name ?? "tool"} step={steps[i]} onSelect={ctx.onSelect} />)}
                </div>
              )}
              {!said && !calls.length && <CardEmpty>The reply was empty.</CardEmpty>}
            </>
          )}
        </PayloadCard>
      )}
    </>
  );
}

/* ------------------------------ a wait on a person ------------------------------ */

/** What the run stopped for: a question to answer, or a tool call to approve. */
interface WaitInput {
  question?: string | null;
  call?: { name?: string; args?: Record<string, unknown> } | null;
}

/** The run stopped for a person. The trace holds what was asked and how long the wait was;
    what they decided arrives as the next attempt's input, so it is not claimed here. */
export function WaitCards({ span, ctx }: { span: Span; ctx: StepContext }) {
  const asked = (span.record?.input ?? {}) as WaitInput;
  const approval = span.name === "approval";
  return (
    <>
      <PayloadCard ctx={ctx} label={approval ? "Asked to approve" : "Asked"} raw={span.record?.input}>
        {asked.question && <div className="px-4 py-3 text-[13.5px] leading-[1.6] text-[var(--text-primary)] whitespace-pre-wrap break-words">{asked.question}</div>}
        {asked.call && (
          <div className="min-w-0">
            <div className="px-4 pt-2.5 font-mono text-[12.5px] font-medium text-[var(--text-primary)]">{asked.call.name}</div>
            {asked.call.args && Object.keys(asked.call.args).length > 0 ? <Fields data={asked.call.args} /> : <CardEmpty>No arguments.</CardEmpty>}
          </div>
        )}
        {!asked.question && !asked.call && <CardEmpty>The trace didn't keep what was asked.</CardEmpty>}
      </PayloadCard>
      <ListCard label="Outcome">
        {span.status === "pending" ? (
          <CardEmpty>{approval ? "Waiting for approval. The call hasn't run." : "Waiting for an answer."}</CardEmpty>
        ) : wasStopped(span) ? (
          <CardEmpty>The run was stopped while it waited.</CardEmpty>
        ) : (
          <CardEmpty>{approval ? "Decided" : "Answered"} after {fmtMs(span.dur)}; the run carried on.</CardEmpty>
        )}
      </ListCard>
    </>
  );
}

/* ---------------------------------- a run ---------------------------------- */

/** Where the run's wall-clock went: model calls (blue, as they are everywhere), tools, and the
    gaps between steps (amber: waiting). */
function TimeSplit({ analytics, idle, total }: { analytics: TraceAnalytics; idle: number; total: number }) {
  const parts = ([
    ["Model calls", analytics.llmMs, "bg-[var(--info)]"],
    ["Tools", analytics.toolMs, "bg-[var(--text-quaternary)]"],
    ["Idle", idle, "bg-[var(--warn)]"],
  ] as const).filter(([, ms]) => ms > 0);
  const sum = parts.reduce((n, [, ms]) => n + ms, 0);
  if (!sum) return <CardEmpty>Nothing was timed yet.</CardEmpty>;
  return (
    <div className="px-4 pt-3.5 pb-3">
      <div className="flex h-2 gap-[2px] rounded-full overflow-hidden">
        {parts.map(([label, ms, tone]) => <span key={label} className={tone} style={{ width: `${(ms / sum) * 100}%` }} />)}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2.5 text-[12px] text-[var(--text-tertiary)]">
        {parts.map(([label, ms, tone]) => (
          <span key={label} className="inline-flex items-center gap-1.5">
            <span className={`w-2 h-2 rounded-[2px] ${tone}`} />
            {label} <span className="font-mono tabular-nums text-[var(--text-primary)]">{fmtMs(ms)}</span>
          </span>
        ))}
      </div>
      {sum > total * 1.02 && (
        <p className="m-0 mt-2 text-[11.5px] text-[var(--text-quaternary)]">Steps that ran side by side overlap, so these add up to more than the run's {fmtMs(total)}.</p>
      )}
    </div>
  );
}

const answerOf = (span: Span) => textOf((span.io.output as AssistantPayload | null)?.content);

/** Why a run has no answer, by how it stands. */
const NO_ANSWER: Record<string, string> = {
  running: "No answer yet.", queued: "No answer yet.", attention: "No answer yet: it is waiting on a person.",
  halted: "It was stopped before it answered.", error: "It failed before it answered.",
};

export function RunCards({ span, ctx }: { span: Span; ctx: StepContext }) {
  const prompt = (span.io.input as { prompt?: string } | null)?.prompt;
  const answer = answerOf(span);
  const error = ctx.run.recorded?.error;
  const { analytics, effects, spend, idle } = ctx.overview;
  return (
    <>
      {error && <ErrorCard text={error} />}
      {prompt && (
        <ListCard label="Asked" action={<CopyButton text={prompt} label="Copy question" />}>
          <div className="p-3.5">
            <span className="inline-block max-w-full rounded-[16px] bg-[var(--surface-2)] px-3.5 py-[9px] text-[14px] leading-[1.5] text-[var(--text-primary)] whitespace-pre-wrap break-words">{prompt}</span>
          </div>
        </ListCard>
      )}
      <PayloadCard ctx={ctx} label="Answer" raw={ctx.run.recorded?.output ?? undefined} copy={answer || undefined}>
        {answer ? (
          <div className="px-4 py-3 text-[13.5px] leading-[1.6] text-[var(--text-primary)] whitespace-pre-wrap break-words">{answer}</div>
        ) : (
          <CardEmpty>{NO_ANSWER[ctx.run.status] ?? "The run gave no answer."}</CardEmpty>
        )}
      </PayloadCard>
      <ListCard label={<>What it changed <span className="font-normal text-[var(--text-quaternary)]">{effects.length || ""}</span></>}>
        <EffectLedger effects={effects} onSelect={ctx.onSelect} inCard />
      </ListCard>
      <ListCard label="Where the time went">
        <TimeSplit analytics={analytics} idle={idle} total={span.dur} />
      </ListCard>
      {spend.items.length > 0 && (
        <ListCard label={<>Spend by step <span className="font-mono font-normal text-[var(--text-quaternary)]">{money(spend.total)}</span></>}>
          <div className="p-1.5"><SpendList items={spend.items} total={spend.total} onSelect={ctx.onSelect} /></div>
        </ListCard>
      )}
    </>
  );
}

/** One attempt of a run that took more than one: why it ended, what it was asked, what it answered. */
export function AttemptCards({ span }: { span: Span }) {
  const asked = (span.io.input as TurnInputPayload | null)?.text;
  const answer = answerOf(span);
  return (
    <>
      {span.status === "error" && span.meta.status_message && <ErrorCard text={span.meta.status_message} />}
      <ListCard label="Asked">
        {asked ? <div className="px-4 py-3 text-[13.5px] leading-[1.6] whitespace-pre-wrap break-words">{asked}</div> : <CardEmpty>It carried on where the last attempt stopped.</CardEmpty>}
      </ListCard>
      <ListCard label="Answer">
        {answer ? <div className="px-4 py-3 text-[13.5px] leading-[1.6] whitespace-pre-wrap break-words">{answer}</div> : <CardEmpty>No answer in this attempt.</CardEmpty>}
      </ListCard>
    </>
  );
}

/** Any other kind of step: its input and output in the kind's own view. */
export function OtherCards({ span, ctx }: { span: Span; ctx: StepContext }) {
  const record = span.record;
  return (
    <>
      {span.status === "error" && span.meta.status_message && <ErrorCard text={span.meta.status_message} />}
      <PayloadCard ctx={ctx} label="Input" raw={record ? record.input : span.io.input}>
        <div className="px-4 py-2 min-w-0"><PrettyView span={span} io={span.io} which="input" /></div>
      </PayloadCard>
      <PayloadCard ctx={ctx} label="Output" raw={record ? record.output : span.io.output}>
        <div className="px-4 py-2 min-w-0"><PrettyView span={span} io={span.io} which="output" /></div>
      </PayloadCard>
    </>
  );
}
